import { useEffect, useState } from "react";
import { X, Mail, Phone, Building2, AlertTriangle, CheckCircle2, Globe } from "lucide-react";
import api from "../lib/api";
import MemberAccess from "./MemberAccess";
import MemberUsage from "./MemberUsage";
import { WifiPasscode } from "./WifiPasscode";

export const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`);
export const fmtD = (iso) => { if (!iso) return "—"; const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); };

const STATUS = {
  active: ["Active", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  notice: ["On notice", "bg-amber-50 text-amber-800 border-amber-200"],
  upcoming: ["Starting soon", "bg-sky-50 text-sky-700 border-sky-200"],
};
export function StatusPill({ status }) {
  const [label, cls] = STATUS[status] || [status, "bg-brand-hover text-brand-muted border-brand-border"];
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`}>{label}</span>;
}

const Label = ({ children }) => <p className="text-[10px] uppercase tracking-[0.14em] text-brand-tertiary mb-2">{children}</p>;

function Contract({ c }) {
  return (
    <div className="rounded-lg border border-brand-border bg-brand-surface p-4" data-testid={`member-contract-${c.contract_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-brand-ink">{c.name}</p>
          <p className="text-xs text-brand-muted mt-0.5">{c.location}</p>
        </div>
        <StatusPill status={c.status} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
        <div><p className="text-[10px] text-brand-tertiary uppercase tracking-wide">Monthly</p><p className="text-brand-ink font-medium">{money(c.mrr)}</p></div>
        <div><p className="text-[10px] text-brand-tertiary uppercase tracking-wide">Start</p><p className="text-brand-ink">{fmtD(c.move_in)}</p></div>
        <div><p className="text-[10px] text-brand-tertiary uppercase tracking-wide">End</p><p className="text-brand-ink">{fmtD(c.move_out)}</p></div>
        <div><p className="text-[10px] text-brand-tertiary uppercase tracking-wide">Term</p><p className="text-brand-ink">{c.duration_months ? `${c.duration_months} mo` : "—"}{c.auto_renew === "Yes" ? " · auto-renew" : ""}</p></div>
      </div>
      {c.notice_date && <p className="text-xs text-amber-800 mt-3">Gave notice — leaving {fmtD(c.notice_date)}</p>}
      {c.offices.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {c.offices.map((o, i) => <span key={i} className="text-xs rounded-md bg-brand-ink text-brand-page px-2 py-1">{o.type || "Office"} {o.number}{o.desks ? ` · ${o.desks} desks` : ""}</span>)}
        </div>
      )}
      {c.items.length > 0 && (
        <ul className="mt-3 space-y-1">
          {c.items.map((it, i) => <li key={i} className="text-xs text-brand-muted flex justify-between gap-3"><span>{it.qty > 1 ? `${it.qty}× ` : ""}{it.name}</span><span>{it.price ? money(it.price) : "Included"}</span></li>)}
        </ul>
      )}
    </div>
  );
}

function Billing({ b }) {
  const flag = (ok, yes, no) => (
    <span className={`inline-flex items-center gap-1 text-xs ${ok ? "text-emerald-700" : "text-brand-muted"}`}>
      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : null}{ok ? yes : no}
    </span>
  );
  return (
    <div className="rounded-lg border border-brand-border bg-brand-surface p-4 space-y-2" data-testid="member-billing">
      {b.delinquent
        ? <p className="flex items-center gap-1.5 text-sm font-medium text-rose-700" data-testid="member-delinquent"><AlertTriangle className="h-4 w-4" /> Delinquent balance</p>
        : <p className="flex items-center gap-1.5 text-sm text-emerald-700" data-testid="member-good-standing"><CheckCircle2 className="h-4 w-4" /> In good standing</p>}
      <div className="flex flex-wrap gap-x-5 gap-y-1">
        {flag(b.autopay, "Auto-pay on", "Auto-pay off")}
        {flag(b.autobill, "Auto-bill on", "Auto-bill off")}
        {b.tax_exempt && <span className="text-xs text-brand-muted">Tax exempt</span>}
      </div>
      {b.billing_email && <p className="text-xs text-brand-muted">Invoices to {b.billing_email}</p>}
    </div>
  );
}

export default function MemberDetail({ companyId, onClose }) {
  const [m, setM] = useState(null);
  useEffect(() => { api.get(`/members/${companyId}`).then(({ data }) => setM(data)).catch(() => setM(false)); }, [companyId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose} data-testid="member-detail">
      <div className="bg-brand-page w-full max-w-2xl h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-brand-page border-b border-brand-border px-6 py-4 flex items-center justify-between z-10">
          <div className="min-w-0">
            <h3 className="font-serif text-2xl text-brand-ink truncate" data-testid="member-detail-name">{m ? m.company_name : "Member"}</h3>
            {m && <p className="text-xs text-brand-muted mt-0.5">{m.locations.join(" · ")}{m.company_type ? ` · ${m.company_type}` : ""}</p>}
          </div>
          <button onClick={onClose} data-testid="member-detail-close" className="text-brand-muted hover:text-brand-ink"><X className="h-5 w-5" /></button>
        </div>
        {m === null && <div className="p-6 space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-16 rounded-lg bg-brand-surface border border-brand-border animate-pulse" />)}</div>}
        {m === false && <p className="p-6 text-sm text-brand-muted">Couldn't load this member.</p>}
        {m && (
          <div className="p-6 space-y-7">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div><Label>Offices</Label><p className="text-lg text-brand-ink" data-testid="member-detail-offices">{m.offices.map((o) => o.number).join(", ") || "Coworking"}</p></div>
              <div><Label>Monthly total</Label><p className="text-lg text-brand-ink" data-testid="member-detail-mrr">{money(m.mrr)}</p></div>
              <div><Label>Account manager</Label><p className="text-sm text-brand-ink">{m.account_manager || "—"}</p></div>
            </div>
            <div><Label>Billing</Label><Billing b={m.billing} /></div>
            {m.status === "former" ? (
              <p className="text-xs text-brand-tertiary" data-testid="member-former-note">Former member — room usage and door access aren't available.</p>
            ) : (<>
            <div><Label>Room usage</Label><MemberUsage companyId={m.company_id} /></div>
            <div><Label>Door access (Kisi)</Label><MemberAccess companyId={m.company_id} /></div>
            </>)}
            <div>
              <Label>Contracts ({m.contracts.length})</Label>
              <div className="space-y-3">{m.contracts.map((c) => <Contract key={c.contract_id} c={c} />)}</div>
            </div>
            <div>
              <Label>Contacts ({m.contacts.length})</Label>
              <div className="rounded-lg border border-brand-border divide-y divide-brand-border bg-brand-surface" data-testid="member-contacts">
                {m.contacts.map((c) => (
                  <div key={c.id} className="px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1">
                    <p className="text-sm text-brand-ink font-medium min-w-[140px]">{c.name}{c.primary && <span className="ml-2 text-[10px] uppercase tracking-wide text-brand-gold">Primary</span>}</p>
                    {c.email && <a href={`mailto:${c.email}`} className="text-xs text-brand-muted hover:text-brand-primary inline-flex items-center gap-1"><Mail className="h-3 w-3" />{c.email}</a>}
                    {c.phone && <a href={`tel:${c.phone}`} className="text-xs text-brand-muted hover:text-brand-primary inline-flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone}</a>}
                    {c.status && c.status !== "Active" && <span className="text-[10px] text-brand-tertiary">{c.status}</span>}
                    {c.email && m.status !== "former" && <WifiPasscode companyId={m.company_id} email={c.email} />}
                  </div>
                ))}
                {!m.contacts.length && <p className="px-4 py-3 text-sm text-brand-tertiary">No contacts in Yardi.</p>}
              </div>
            </div>
            {(m.website || m.mailbox || m.industry) && (
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-brand-muted">
                {m.website && <span className="inline-flex items-center gap-1"><Globe className="h-3 w-3" />{m.website}</span>}
                {m.mailbox && <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" />Mailbox {m.mailbox}</span>}
                {m.industry && m.industry !== "-" && <span>{m.industry}</span>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
